import { useEffect, useRef } from "react";
import type { LocalVideoTrack } from "twilio-video";
import { useVideoCalling } from "./useVideoCallingContext";
import type { VideoCallingSlice, BackgroundEffect } from "../types";

type ProcessorsModule = typeof import("@twilio/video-processors");
type BackgroundProcessor =
  | InstanceType<ProcessorsModule["GaussianBlurBackgroundProcessor"]>
  | InstanceType<ProcessorsModule["VirtualBackgroundProcessor"]>;

let modulePromise: Promise<ProcessorsModule> | null = null;
export interface UseBackgroundEffectOptions {
  /**
   * The app's public base path (Vite's `import.meta.env.BASE_URL` in a Vite app), used to locate the Twilio
   * video-processor assets at `<base>twilio-video-processors`. Defaults to "/". Passed in rather than read
   * here so this package stays free of Vite-only APIs and usable outside Vite.
   */
  readonly assetsBaseUrl?: string;
}

const loadVideoProcessors = (): Promise<ProcessorsModule> => {
  if (!modulePromise) {
    modulePromise = import("@twilio/video-processors");
  }
  return modulePromise;
};

/**
 * Loads an image into a CORS-clean HTMLImageElement. The virtual background
 * processor composites the image onto a canvas, so it must be cross-origin clean
 * (crossOrigin set before src) or the output frame comes out empty.
 */
const loadImageElement = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load background image: ${url}`));
    img.src = url;
  });

const detachProcessor = (track: LocalVideoTrack | null, processor: BackgroundProcessor) => {
  if (!track) return;
  try {
    track.removeProcessor(processor);
  } catch {
    // Detaching from a stopped/disposed track is a no-op
  }
};

/**
 * Manages the lifecycle of Twilio's background effect processors (blur or image).
 *
 * Lazy-imports `@twilio/video-processors` once on mount, probes browser support,
 * holds a single processor instance in a ref, and synchronizes the processor
 * attachment with `[localVideoTrack, backgroundEffect, isEffectSupported, backgroundImageURL]`.
 * Rebuilds the processor when the effect type (or image URL) changes, and re-attaches
 * automatically when the user switches camera devices mid-call.
 *
 * `backgroundImageURL` is supplied only by the provider webapp; when omitted, the
 * "image" effect is unavailable and any persisted "image" preference falls back to "none".
 *
 * Should be called exactly once per call window.
 */
export const useBackgroundEffect = (backgroundImageURL?: string, options?: UseBackgroundEffectOptions) => {
  const videoCalling = useVideoCalling();
  const assetsBaseUrlRef = useRef(options?.assetsBaseUrl ?? "/");
  assetsBaseUrlRef.current = options?.assetsBaseUrl ?? "/";

  const videoCallingRef = useRef<VideoCallingSlice>(videoCalling);
  videoCallingRef.current = videoCalling;

  const processorRef = useRef<BackgroundProcessor | null>(null);
  const processorEffectRef = useRef<BackgroundEffect>("none");
  const processorImageUrlRef = useRef<string | null>(null);
  const modelLoadedRef = useRef(false);
  const previousTrackRef = useRef<LocalVideoTrack | null>(null);

  // Cache the loaded image element, keyed by URL.
  const imageUrlRef = useRef<string | null>(null);
  const imageElementRef = useRef<HTMLImageElement | null>(null);

  const { localVideoTrack, backgroundEffect, isEffectSupported } = videoCalling;

  // Sync the injected image URL into the slice so the UI can derive availability.
  useEffect(() => {
    videoCallingRef.current.setBackgroundImageURL(backgroundImageURL ?? null);
  }, [backgroundImageURL]);

  // Lazy-load the library once and probe support; detach + release the processor on unmount.
  useEffect(() => {
    let cancelled = false;
    loadVideoProcessors()
      .then((mod) => {
        if (cancelled) return;
        videoCallingRef.current.setEffectSupported(mod.isSupported);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Failed to load @twilio/video-processors:", error);
        videoCallingRef.current.setEffectSupported(false);
      });
    return () => {
      cancelled = true;
      if (processorRef.current && previousTrackRef.current) {
        detachProcessor(previousTrackRef.current, processorRef.current);
      }
      processorRef.current = null;
      previousTrackRef.current = null;
    };
  }, []);

  // Sync the processor with the current track + effect.
  useEffect(() => {
    if (!isEffectSupported) return;

    const vc = videoCallingRef.current;
    let cancelled = false;

    // If the active track changed, detach from the previous one.
    if (previousTrackRef.current && previousTrackRef.current !== localVideoTrack && processorRef.current) {
      detachProcessor(previousTrackRef.current, processorRef.current);
    }
    previousTrackRef.current = localVideoTrack;

    const disposeProcessor = () => {
      if (processorRef.current && localVideoTrack && localVideoTrack.processor === processorRef.current) {
        detachProcessor(localVideoTrack, processorRef.current);
      }
      processorRef.current = null;
      processorEffectRef.current = "none";
      processorImageUrlRef.current = null;
      modelLoadedRef.current = false;
    };

    if (backgroundEffect === "none" || !localVideoTrack) {
      // Detach the current processor but keep the instance for reuse.
      if (processorRef.current && localVideoTrack && localVideoTrack.processor === processorRef.current) {
        detachProcessor(localVideoTrack, processorRef.current);
      }
      return () => {
        cancelled = true;
      };
    }

    const apply = async () => {
      try {
        const mod = await loadVideoProcessors();
        if (cancelled) return;

        // Resolve the image element for image mode. Fall back to "none" if unavailable.
        let imageElement: HTMLImageElement | null = null;
        if (backgroundEffect === "image") {
          if (!backgroundImageURL) {
            vc.setBackgroundEffect("none");
            return;
          }
          if (imageUrlRef.current === backgroundImageURL && imageElementRef.current) {
            imageElement = imageElementRef.current;
          } else {
            imageElement = await loadImageElement(backgroundImageURL);
            if (cancelled) return;
            imageUrlRef.current = backgroundImageURL;
            imageElementRef.current = imageElement;
          }
        }

        // Rebuild when the effect type changes, or when the image URL changes for image mode.
        const needsRebuild =
          !!processorRef.current &&
          (processorEffectRef.current !== backgroundEffect ||
            (backgroundEffect === "image" && processorImageUrlRef.current !== backgroundImageURL));
        if (needsRebuild) {
          disposeProcessor();
        }

        if (!processorRef.current) {
          const baseUrl = assetsBaseUrlRef.current;
          const assetsPath = `${baseUrl}twilio-video-processors`;
          if (backgroundEffect === "blur") {
            processorRef.current = new mod.GaussianBlurBackgroundProcessor({ assetsPath });
          } else if (imageElement) {
            processorRef.current = new mod.VirtualBackgroundProcessor({
              assetsPath,
              backgroundImage: imageElement,
              fitType: mod.ImageFit.Cover,
            });
            processorImageUrlRef.current = backgroundImageURL ?? null;
          } else {
            vc.setBackgroundEffect("none");
            return;
          }
          processorEffectRef.current = backgroundEffect;
          modelLoadedRef.current = false;
        }

        if (!modelLoadedRef.current) {
          vc.setEffectLoading(true);
          await processorRef.current.loadModel();
          modelLoadedRef.current = true;
          vc.setEffectLoading(false);
        }
        if (cancelled) return;

        // Don't re-attach if the track already has our processor.
        if (localVideoTrack.processor === processorRef.current) return;

        localVideoTrack.addProcessor(processorRef.current, {
          inputFrameBufferType: "videoframe",
          outputFrameBufferContextType: "bitmaprenderer",
        });
      } catch (error) {
        console.error("Failed to apply background effect:", error);
        vc.setEffectLoading(false);
        vc.setBackgroundEffect("none");
      }
    };
    apply();

    return () => {
      cancelled = true;
    };
  }, [localVideoTrack, backgroundEffect, isEffectSupported, backgroundImageURL]);
};
